import React, { useState } from 'react';
import { Header } from '@/components/layout/Header';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PlatformFooter } from '@/components/common/PageElements';
import { 
  Building2, 
  TrendingUp,
  Globe,
  DollarSign,
  ArrowRight,
  Mail,
  CheckCircle,
  Lock
} from 'lucide-react';

const PreviewBlock = ({ icon: Icon, title, description, metrics }) => (
  <Card className="bg-card/50 border-border/50 hover:border-primary/30 transition-colors">
    <CardContent className="p-6">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
          <Icon className="w-6 h-6 text-primary" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-lg font-semibold text-foreground">{title}</h3>
            <Lock className="w-3.5 h-3.5 text-muted-foreground" />
          </div>
          <p className="text-sm text-muted-foreground mb-4">{description}</p>
          <div className="flex flex-wrap gap-2">
            {metrics.map((metric, i) => (
              <Badge key={i} variant="outline" className="text-xs bg-muted/30">
                {metric}
              </Badge>
            ))}
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function TeamsPage() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (email) {
      setSubmitted(true);
      setEmail('');
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="clubs-page">
      <Header />
      
      <main className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 py-8 sm:py-12">
        {/* Phase Badge */}
        <div className="flex justify-center mb-6">
          <Badge className="bg-primary/10 text-primary border-primary/20 px-4 py-1.5">
            Phase 2 Expansion Module
          </Badge>
        </div>

        {/* Page Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-14 h-14 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
              <Building2 className="w-7 h-7 text-primary" />
            </div>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-3">
            Club-Level Brand Intelligence
          </h1>
          <p className="text-lg text-muted-foreground">
            Coming Phase 2
          </p>
        </div>

        {/* Preview Blocks */}
        <div className="space-y-4 mb-12">
          <PreviewBlock
            icon={DollarSign}
            title="Revenue Influence Modeling"
            description="Quantify brand contribution to commercial revenue streams. Model sponsorship, merchandise, and media rights impact across club operations."
            metrics={['Revenue Attribution', 'Commercial Multiplier', 'Brand ROI Index']}
          />
          
          <PreviewBlock
            icon={TrendingUp}
            title="Sponsorship Exposure Index"
            description="Track and benchmark sponsor visibility across competitions. Measure media value, exposure minutes, and category exclusivity performance."
            metrics={['Media Value Equivalent', 'Exposure Minutes', 'Category Performance']}
          />
          
          <PreviewBlock
            icon={Globe}
            title="International Brand Expansion Index"
            description="Assess global market penetration and growth opportunities. Identify high-potential markets and track brand awareness by region."
            metrics={['Market Penetration', 'Growth Velocity', 'Regional Awareness']}
          />
        </div>

        {/* Waitlist CTA */}
        <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20 mb-8">
          <CardContent className="p-8 sm:p-10">
            <div className="text-center max-w-lg mx-auto">
              {!submitted ? (
                <>
                  <h2 className="text-xl font-semibold text-foreground mb-2">
                    Request Early Access for Club Intelligence
                  </h2>
                  <p className="text-sm text-muted-foreground mb-6">
                    Be among the first to access club-level brand analytics when Phase 2 launches.
                  </p>
                  
                  <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                    <div className="flex-1 relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter your email"
                        className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-background border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                        data-testid="waitlist-email-input"
                      />
                    </div>
                    <Button type="submit" data-testid="waitlist-submit-btn">
                      Request Access
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </form>
                </>
              ) : (
                <div className="flex flex-col items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-stable/20 flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-stable" />
                  </div>
                  <h2 className="text-xl font-semibold text-foreground">You're on the list!</h2>
                  <p className="text-sm text-muted-foreground">
                    We'll notify you when Club Intelligence launches.
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Roadmap Info */}
        <div className="text-center">
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Roadmap</p>
          <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-stable" />
              Phase 1: National Teams
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
              Phase 2: Clubs
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-muted-foreground/30" />
              Phase 3: Leagues
            </span>
          </div>
        </div>
      </main>

      <PlatformFooter />
    </div>
  );
}
